import { useEffect, useMemo, useRef, useState } from "react";
import {
  parsePhoneNumberFromString,
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from "libphonenumber-js";
import { cn } from "@/lib/utils";

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  name: string;
  required?: boolean;
  placeholder?: string;
  detectedCountry?: CountryCode | null;
  invalidMessage?: string;
}

export function PhoneInput({
  value,
  onChange,
  name,
  required = false,
  placeholder = "Phone number",
  detectedCountry,
  invalidMessage,
}: PhoneInputProps) {
  const [raw, setRaw] = useState("");
  const [country, setCountry] = useState<CountryCode>("US");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (detectedCountry) setCountry(detectedCountry);
  }, [detectedCountry]);

  const countries = useMemo(
    () =>
      getCountries()
        .map((c) => ({
          c,
          name: new Intl.DisplayNames(["en"], { type: "region" }).of(c) ?? c,
          dial: `+${getCountryCallingCode(c)}`,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  const update = (text: string, c: CountryCode = country) => {
    setRaw(text);
    const parsed = parsePhoneNumberFromString(text, c);
    onChange(parsed?.isValid() ? parsed.number : "");
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = countries.filter((x) =>
    x.name.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div className="relative flex gap-2">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="shrink-0 rounded-lg border border-input bg-muted px-2.5 py-2 text-sm font-medium text-foreground"
      >
        {country} {`+${getCountryCallingCode(country)}`}
      </button>
      {open && (
        <div
          ref={listRef}
          role="listbox"
          className="absolute left-0 top-full z-20 mt-1 w-64 max-h-56 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg"
        >
          <input
            autoFocus
            placeholder="Search country"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="mb-1 w-full rounded border border-input bg-background px-2 py-1 text-sm"
          />
          {filtered.map((x) => (
            <div
              key={x.c}
              role="option"
              aria-selected={x.c === country}
              onClick={() => {
                setCountry(x.c);
                setOpen(false);
                update(raw, x.c);
              }}
              className={cn(
                "cursor-pointer rounded px-2 py-1.5 text-sm hover:bg-muted",
                x.c === country && "bg-muted",
              )}
            >
              {x.name} {x.dial}
            </div>
          ))}
        </div>
      )}
      <input
        type="tel"
        name={name}
        required={required}
        placeholder={placeholder}
        value={raw}
        onChange={(e) => update(e.target.value)}
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      />
      {invalidMessage && (
        <p className="absolute -bottom-5 left-0 text-xs text-destructive">
          {invalidMessage}
        </p>
      )}
    </div>
  );
}
